import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';

// Menu sizing constants: the gap between the field and the menu, the breathing
// room kept against the viewport edge, and the tallest/shortest the menu may be.
const MENU_GAP = 4;
const VIEWPORT_MARGIN = 8;
const MENU_MAX_HEIGHT = 250;
const MENU_MIN_HEIGHT = 120;
// A downward menu is what a dropdown is expected to do, and flipping it above the
// field covers the rest of the form. So the menu only flips when the space below
// can't show a useful number of options -- not merely because it fell a few pixels
// short of MENU_MAX_HEIGHT, which would trade the whole form for a sliver of list.
const MENU_COMFORTABLE_HEIGHT = 160;

const MultiSelectDropdown = ({
  options = [],
  selectedValues = [],
  onChange,
  disabled = false,
  placeholder = "Select options"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({
    top: 0,
    bottom: 0,
    left: 0,
    width: 0,
    maxHeight: MENU_MAX_HEIGHT,
    openUp: false,
  });
  const dropdownRef = useRef(null);

  // Keep the fixed-position menu anchored to the field as the user scrolls or
  // resizes (the menu is rendered with position:fixed to escape the modal's
  // overflow clipping, so its coordinates must be recalculated on those events).
  // The menu is also flipped above the field and clamped to the space actually
  // available, so it can never render past the bottom edge of the viewport where
  // its options would be invisible and unreachable.
  useLayoutEffect(() => {
    if (!isOpen) return undefined;

    const updatePosition = () => {
      if (!dropdownRef.current) return;

      const rect = dropdownRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom - MENU_GAP - VIEWPORT_MARGIN;
      const spaceAbove = rect.top - MENU_GAP - VIEWPORT_MARGIN;

      // Downward is the default and stays that way as long as the space below can
      // show a useful stretch of the list; only a genuinely cramped field flips.
      const openUp =
        spaceBelow < MENU_COMFORTABLE_HEIGHT && spaceAbove > spaceBelow;
      const available = openUp ? spaceAbove : spaceBelow;

      setMenuPosition({
        // Anchor by the edge nearest the field so a short menu still sits flush
        // against it regardless of how many options it holds.
        top: rect.bottom + MENU_GAP,
        bottom: window.innerHeight - rect.top + MENU_GAP,
        left: rect.left,
        width: rect.width,
        maxHeight: Math.max(MENU_MIN_HEIGHT, Math.min(MENU_MAX_HEIGHT, available)),
        openUp,
      });
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleSelectOption = (optionName) => {
    if (selectedValues.includes(optionName)) {
      onChange(selectedValues.filter(v => v !== optionName));
    } else {
      onChange([...selectedValues, optionName]);
    }
  };

  const isAllSelected =
    options.length > 0 && options.every(opt => selectedValues.includes(opt.name));

  const handleSelectAll = () => {
    if (isAllSelected) {
      onChange([]);
    } else {
      onChange(options.map(opt => opt.name));
    }
  };

  // Show the same human-readable label used in the option rows, falling back to
  // the raw value when the option isn't in the loaded list.
  const getLabel = (value) => {
    const option = options.find(opt => opt.name === value);
    return option ? (option.description || option.name) : value;
  };

  return (
    <div ref={dropdownRef} style={{ position: 'relative', width: '100%' }}>
      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        style={{
          width: '100%',
          padding: '11px 14px',
          fontSize: '14px',
          border: '1px solid #d1d5db',
          borderRadius: '8px',
          backgroundColor: disabled ? '#f3f4f6' : 'white',
          color: disabled ? '#9ca3af' : '#374151',
          cursor: disabled ? 'not-allowed' : 'pointer',
          textAlign: 'left',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          transition: 'all 0.2s',
          minHeight: '44px',
        }}
      >
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '6px',
          flex: 1,
          alignItems: 'center',
        }}>
          {selectedValues.length === 0 ? (
            <span style={{ color: '#9ca3af' }}>{placeholder}</span>
          ) : (
            <>
              {selectedValues.slice(0, 4).map((value) => (
                <span
                  key={value}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '2px 8px',
                    fontSize: '12px',
                    fontWeight: '500',
                    borderRadius: '9999px',
                    backgroundColor: '#dbeafe',
                    color: '#1e40af',
                  }}
                >
                  {getLabel(value)}
                </span>
              ))}
              {selectedValues.length > 4 && (
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '2px 8px',
                  fontSize: '12px',
                  fontWeight: '500',
                  borderRadius: '9999px',
                  backgroundColor: '#f3f4f6',
                  color: '#4b5563',
                }}>
                  +{selectedValues.length - 4} more
                </span>
              )}
            </>
          )}
        </div>
        <svg
          style={{
            width: '16px',
            height: '16px',
            marginLeft: '8px',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
          }}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && !disabled && (
        <div
          style={{
            position: 'fixed',
            ...(menuPosition.openUp
              ? { bottom: menuPosition.bottom }
              : { top: menuPosition.top }),
            left: menuPosition.left,
            width: menuPosition.width || 'auto',
            backgroundColor: 'white',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            zIndex: 10002,
            maxHeight: menuPosition.maxHeight,
            overflowY: 'auto',
          }}
        >
          {options.length > 0 && (
            <button
              onClick={handleSelectAll}
              type="button"
              style={{
                padding: '10px 14px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                borderBottom: '1px solid #e5e7eb',
                backgroundColor: '#f9fafb',
                fontWeight: '600',
                fontSize: '13px',
                color: '#374151',
                border: 'none',
                width: '100%',
                textAlign: 'left',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
            >
              <input
                type="checkbox"
                checked={isAllSelected}
                onChange={() => { }}
                style={{
                  marginRight: '10px',
                  cursor: 'pointer',
                  width: '16px',
                  height: '16px',
                }}
              />
              <span>{isAllSelected ? 'Unselect All' : 'Select All'}</span>
            </button>
          )}

          {options.length === 0 ? (
            <div style={{ padding: '16px', textAlign: 'center', color: '#9ca3af', fontSize: '14px' }}>
              No options available
            </div>
          ) : (
            options.map((option) => (
              <button
                key={option.object_id}
                onClick={() => handleSelectOption(option.name)}
                type="button"
                style={{
                  padding: '10px 14px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '14px',
                  color: '#374151',
                  transition: 'background-color 0.15s',
                  border: 'none',
                  backgroundColor: 'white',
                  width: '100%',
                  textAlign: 'left',
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
              >
                <input
                  type="checkbox"
                  checked={selectedValues.includes(option.name)}
                  onChange={() => { }}
                  style={{
                    marginRight: '10px',
                    cursor: 'pointer',
                    width: '16px',
                    height: '16px',
                  }}
                />
                <div style={{ fontWeight: '500' }}>
                  {option.description || option.name}
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

MultiSelectDropdown.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      object_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      description: PropTypes.string,
    })
  ),
  selectedValues: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
};

export default MultiSelectDropdown;